import { Autocomplete, TextField } from '@mui/material';
import { useMemo } from 'react';
import useSWR from 'swr';
import { request } from '@/api/client';

/**
 * Dropdown of tenant types sourced from the platform's master table at
 * `/admin/tenant-types`. Falls back to a built-in list if the endpoint
 * returns an error (so the form keeps working before the backend ships
 * the master collection).
 *
 * The component stores the type's `code` on the form so the existing
 * `Tenant.organizationType` field stays a stable string. When the
 * backend later switches to id-references, swap to `id` here without
 * rippling through every form.
 */

export interface TenantTypeOption {
  id?: string;
  code: string;
  name: string;
  active?: boolean;
}

const FALLBACK: TenantTypeOption[] = [
  { code: 'SOFTWARE_COMPANY', name: 'Software Company' },
  { code: 'HOSPITAL',         name: 'Hospital' },
  { code: 'CLINIC',           name: 'Clinic' },
  { code: 'DIAGNOSTIC_CENTER', name: 'Diagnostic Center' },
  { code: 'LAB',              name: 'Lab' },
  { code: 'PHARMACY',         name: 'Pharmacy' },
  { code: 'OWN',              name: 'Own' },
  { code: 'OTHER',            name: 'Other' },
];

export function TenantTypeSelect({
  value,
  onChange,
  required = false,
  disabled = false,
  fullWidth = true,
  helperText,
}: {
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
}) {
  const { data, error } = useSWR<TenantTypeOption[]>(
    '/admin/tenant-types',
    () => request<TenantTypeOption[]>({ url: '/admin/tenant-types', method: 'GET' }),
    { dedupingInterval: 60_000 },
  );

  const options = useMemo<TenantTypeOption[]>(() => {
    // If backend doesn't have the endpoint yet, keep working with the fallback.
    if (error || !data) return FALLBACK;
    const active = data.filter((o) => o.active !== false);
    return active.length ? active : FALLBACK;
  }, [data, error]);

  const selected = useMemo(
    () => options.find((o) => o.code === value) ?? null,
    [options, value],
  );

  return (
    <Autocomplete<TenantTypeOption, false, false, true>
      size="small"
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      value={selected}
      freeSolo
      onChange={(_, next) => {
        if (typeof next === 'string') onChange(next);
        else onChange(next?.code ?? '');
      }}
      onInputChange={(_, val, reason) => {
        // Allow free-typed codes (so legacy values like "HOSPITAL" not in
        // the master still round-trip cleanly).
        if (reason === 'input') onChange(val.trim());
      }}
      autoHighlight
      openOnFocus
      filterOptions={(opts, state) => {
        const lc = state.inputValue.trim().toLowerCase();
        if (!lc) return opts;
        return opts.filter(
          (o) => o.name.toLowerCase().includes(lc) || o.code.toLowerCase().includes(lc),
        );
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Type"
          required={required}
          helperText={helperText ?? (error ? 'Master table unavailable — using built-in list' : undefined)}
        />
      )}
    />
  );
}
